import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HelpCircle, BookOpen, Search } from 'lucide-react';
import { visibleGuides } from '../guides';
import { useAuth } from '../context/AuthContext';

// Help nella barra in alto (richiesta dell'utente, 26/10/2026): due righe sulla pagina corrente,
// le guide operative già presenti in /guides e la scorciatoia della ricerca globale.
const PAGE_HELP: Record<string, string> = {
  '/overview': 'Summary of detections, assets and infrastructure for the selected tenant. Click a live tile to open its page.',
  '/soc-ops': 'Live operations board for the analysts on shift.',
  '/incidents': 'Incidents group correlated alerts on the same host or user. Default view: last 24 hours, refreshed every 5 minutes.',
  '/alerts': 'Raw detections produced by the rules and the integrations. Default view: last 24 hours, refreshed every 5 minutes.',
  '/logs': 'Raw events received from the brokers. Default view: last 24 hours, refreshed every 5 minutes.',
  '/asset-center': 'Devices seen on the customer networks, with reachability, EDR coverage and risk.',
  '/log-sources': 'Devices sending logs to the brokers. Give them a label to recognise them across the console.',
  '/data-quality': 'Gaps and anomalies in the incoming data: silent sources, parsing errors, clock drift.',
  '/sites-brokers': 'Customer sites and the brokers that collect their logs.',
  '/notifications': 'Every operational notification, open and resolved.',
};

export default function HelpMenu() {
  const location = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(false), [location.pathname]);
  const pageHelp = PAGE_HELP[location.pathname] ?? PAGE_HELP[`/${location.pathname.split('/')[1]}`];

  return (
    <div className="relative">
      <button onClick={() => setOpen(v => !v)} title="Help"
        className="p-2 rounded-lg text-gray-300 hover:text-white hover:bg-gray-800 transition-colors">
        <HelpCircle size={18} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 w-80 card z-20 text-left">
            {pageHelp && (
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">This page</p>
                <p className="text-sm text-gray-700">{pageHelp}</p>
              </div>
            )}
            <p className="px-4 pt-3 pb-1 text-xs font-semibold text-gray-500 uppercase tracking-wide">Guides</p>
            {visibleGuides(user).map(g => (
              <Link key={g.slug} to={`/guides/${g.slug}`}
                className="flex items-start gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                <BookOpen size={14} className="mt-0.5 shrink-0 text-primary-600" />
                <span>{g.title}<span className="block text-xs text-gray-400">{g.minutes}</span></span>
              </Link>
            ))}
            <Link to="/guides" className="block px-4 py-2 text-xs text-primary-600 hover:underline">All guides</Link>
            <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-500 flex items-center gap-1.5">
              <Search size={12} /> Search everything with <kbd className="px-1 rounded border border-gray-300 bg-gray-50">Ctrl</kbd> + <kbd className="px-1 rounded border border-gray-300 bg-gray-50">K</kbd>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
